import type { Platform, EngagementMetrics } from "../../../../shared/types";
import { cn } from "../../lib/utils";
import { formatCompact, timeAgo } from "../../lib/utils";

interface PerformanceCardProps {
  platform: Platform;
  metrics: EngagementMetrics;
  pieceId: string;
  collectedAt: string;
}

const PLATFORM_CONFIG: Record<
  Platform,
  { label: string; color: string; bgColor: string }
> = {
  linkedin: {
    label: "LinkedIn",
    color: "text-blue-700",
    bgColor: "bg-blue-50 border-blue-200",
  },
  twitter: {
    label: "Twitter / X",
    color: "text-slate-900",
    bgColor: "bg-slate-50 border-slate-200",
  },
  instagram: {
    label: "Instagram",
    color: "text-pink-600",
    bgColor: "bg-pink-50 border-pink-200",
  },
  threads: {
    label: "Threads",
    color: "text-slate-800",
    bgColor: "bg-slate-50 border-slate-200",
  },
  tiktok: {
    label: "TikTok",
    color: "text-slate-900",
    bgColor: "bg-slate-50 border-slate-200",
  },
  youtube: {
    label: "YouTube",
    color: "text-red-600",
    bgColor: "bg-red-50 border-red-200",
  },
};

const METRIC_ITEMS: {
  key: keyof EngagementMetrics;
  label: string;
  icon: string;
}[] = [
  { key: "impressions", label: "Impressions", icon: "M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z M15 12a3 3 0 11-6 0 3 3 0 016 0z" },
  { key: "likes", label: "Likes", icon: "M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" },
  { key: "comments", label: "Comments", icon: "M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 01-.923 1.785A5.969 5.969 0 006 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337z" },
  { key: "shares", label: "Shares", icon: "M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" },
  { key: "clicks", label: "Clicks", icon: "M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zM12 2.25V4.5m5.834.166l-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243l-1.59-1.59" },
];

export function PerformanceCard({
  platform,
  metrics,
  pieceId,
  collectedAt,
}: PerformanceCardProps) {
  const config = PLATFORM_CONFIG[platform];

  return (
    <div
      className={cn(
        "rounded-lg border p-4",
        config.bgColor,
      )}
    >
      {/* Platform header */}
      <div className="mb-3 flex items-center justify-between">
        <span className={cn("text-sm font-semibold", config.color)}>
          {config.label}
        </span>
        <span className="text-xs text-slate-400" title={collectedAt}>
          {timeAgo(collectedAt)}
        </span>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {METRIC_ITEMS.map(({ key, label, icon }) => (
          <div key={key} className="flex items-center gap-1.5">
            <svg
              className="h-3.5 w-3.5 shrink-0 text-slate-400"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d={icon}
              />
            </svg>
            <span className="text-xs text-slate-500">{label}</span>
            <span className="ml-auto text-xs font-semibold text-slate-700">
              {formatCompact(metrics[key])}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
